import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import API from "../../../config/apiconfig";
import { userApiLoader } from "../../../action/loaderActions";
import {
  Avatar,
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Badge,
  Button,
  Chip,
  Tooltip,
  Divider,
  InputAdornment,
  TextField,
  useTheme,
  alpha
} from "@mui/material";
import { 
  Send, 
  ArrowBack, 
  Block, 
  PersonAdd, 
  MoreVert,
  Search as SearchIcon
} from "@mui/icons-material";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  doc,
  updateDoc,
  getDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import Search from "../../../components/common/search/Search";
import axiosInstance from "../../../utils/axios/axiosInstance";
import { useSearchParams, useNavigate } from "react-router-dom";

const Chat = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const [userDetails, setUserDetails] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chatUsers, setChatUsers] = useState([]);
  const [chatPartners, setChatPartners] = useState([]);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [isBlockActionLoading, setIsBlockActionLoading] = useState(false);

  const chatWindowRef = useRef(null);
  const [refreshChat, setRefreshChat] = useState(0);

  const [blockedUsers, setBlockedUsers] = useState([]);
  const [blockedByUsers, setBlockedByUsers] = useState([]);

  // NEW: State for auto-sending product details
  const [autoMessageSent, setAutoMessageSent] = useState(false);
  const [productDetails, setProductDetails] = useState(null);
  const [initialProductChatId, setInitialProductChatId] = useState(null);
  const [urlProcessed, setUrlProcessed] = useState(false);

  // FIXED: Handle selectedChat for both existing partners and new URL users
  const selectedChat = chatPartners.find(p => p.student_id === selectedChatId) || 
    (selectedChatId && {
      student_id: selectedChatId,
      name: productDetails?.sellerName || "User",
      profile_picture: "",
      lastMessage: "",
      lastMessageTime: null,
      isNewUser: true
    });

  const isBlocked = blockedUsers.includes(selectedChatId);
  const isBlockedBy = blockedByUsers.includes(selectedChatId);

  // Custom styles
  const styles = {
    sidebar: {
      background: '#ffffff',
      height: '100%',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    },
    chatWindow: {
      background: '#ffffff',
      height: '100%',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      position: 'relative'
    },
    messageInput: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '25px',
        backgroundColor: '#f8fafc',
        border: 'none',
        '& fieldset': {
          border: '2px solid #e2e8f0',
        },
        '&:hover fieldset': {
          borderColor: '#ce55b9',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#ce55b9',
          borderWidth: '2px',
        }
      }
    },
    chatListItem: {
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      borderRadius: '12px',
      margin: '4px 8px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f8fafc',
        transform: 'translateX(4px)',
      }
    },
    messageBackground: {
      background: '#ffffff',
      minHeight: '400px',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `radial-gradient(circle at 25% 25%, ${alpha('#ce55b9', 0.02)} 0%, transparent 50%),
                         radial-gradient(circle at 75% 75%, ${alpha('#ce55b9', 0.03)} 0%, transparent 50%)`,
        pointerEvents: 'none'
      }
    }
  };

  // NEW: Function to create product message
  const createProductMessage = useCallback((productInfo) => {
    const productMessageText = `🛍️ Hi! I'm interested in your product:

📦 Product: ${productInfo.productName}
💰 Price: ₹${productInfo.productPrice}

🔗 View Product: ${productInfo.productLink}

I'd like to know more about this item. Is it still available?`;

    return productMessageText;
  }, []);

  // NEW: Function to send auto product message
  const sendAutoProductMessage = useCallback(async (productInfo) => {
    if (!selectedChatId || !userDetails?.id || autoMessageSent) return;

    try {
      const chatId = [userDetails.id, selectedChatId].sort().join('_');
      const productMessageText = createProductMessage(productInfo);
      
      await addDoc(collection(db, "messages"), {
        chatId,
        text: productMessageText,
        createdAt: serverTimestamp(),
        userId: userDetails.id,
        userName: userDetails.name,
        userEmail: userDetails.email,
        toUserId: selectedChatId,
        toUserName: productInfo.sellerName || "Seller",
        read: false
      });

      setAutoMessageSent(true);
      console.log('✅ Auto product message sent successfully');
      
    } catch (error) {
      console.error('❌ Error sending auto product message:', error);
    }
  }, [selectedChatId, userDetails, autoMessageSent, createProductMessage]);

  // Get user details on mount
  useEffect(() => {
    const fetchUserDetails = async () => {
      dispatch(userApiLoader({ isApiLoader: true }));
      try {
        const res = await axiosInstance.get(
          `${API.USER_MANAGEMENT_API_BASE_URL}${API.PROFILE_DETAILS}`
        );
        const { user_id, name, email } = res?.data?.data;
        setUserDetails({
          id: user_id.toString(),
          name: name || "Unknown",
          email: email || "noemail@campuscrow.com",
        });
        dispatch(userApiLoader({ isApiLoader: false }));
      } catch (err) {
        console.error("Failed to fetch user details", err);
        dispatch(userApiLoader({ isApiLoader: false }));
      }
    };
    fetchUserDetails();
  }, [dispatch]);

  // Fetch all chat users
  useEffect(() => {
    if (!userDetails?.id) return;

    const fetchAllChatUsers = async () => {
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/chat/user_list`
        );
        if (res.data?.data) {
          setChatUsers(res.data.data.map(user => ({
            ...user,
            student_id: user.student_id.toString()
          })));
        }
      } catch (error) {
        console.error("Error fetching chat users", error);
      }
    };

    fetchAllChatUsers();
  }, [userDetails?.id]);

  // FIXED: URL parameter handler - don't add users to chatPartners, just select them
  useEffect(() => {
    if (!userDetails?.id || urlProcessed) return;

    const urlUserId = searchParams.get("user_id");
    const autoMessage = searchParams.get("auto_message");
    const productName = searchParams.get("product_name");
    const productPrice = searchParams.get("product_price");
    const productLink = searchParams.get("product_link");
    const productId = searchParams.get("product_id");
    const productImage = searchParams.get("product_image");
    const sellerName = searchParams.get("seller_name");

    if (!urlUserId) {
      setUrlProcessed(true);
      return;
    }

    // Check if this is a product inquiry
    const isProductInquiry = autoMessage === 'true' && productName && productPrice && productLink;

    if (isProductInquiry) {
      const productInfo = {
        productName: decodeURIComponent(productName),
        productPrice: productPrice,
        productLink: decodeURIComponent(productLink),
        productId: productId,
        productImage: productImage ? decodeURIComponent(productImage) : '',
        sellerName: sellerName ? decodeURIComponent(sellerName) : 'Seller'
      };
      
      setProductDetails(productInfo);
      setInitialProductChatId(urlUserId.toString());
      console.log('📦 Product details detected:', productInfo);
    }

    // FIXED: Just select the chat, don't add to chatPartners
    // User will be added automatically when first message is sent
    setSelectedChatId(urlUserId.toString());
    setUrlProcessed(true);
  }, [userDetails?.id, urlProcessed, searchParams]);

  // NEW: Auto-send product message when chat is ready (only for initial product inquiry)
  useEffect(() => {
    if (selectedChatId && userDetails?.id && productDetails && !autoMessageSent && 
        !isBlocked && !isBlockedBy && selectedChatId === initialProductChatId) {
      // Add a small delay to ensure the chat is fully loaded
      const timer = setTimeout(() => {
        sendAutoProductMessage(productDetails);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [selectedChatId, userDetails?.id, productDetails, autoMessageSent, isBlocked, isBlockedBy, initialProductChatId, sendAutoProductMessage]);

  // Real-time messages listener for all messages
  useEffect(() => {
    if (!userDetails?.id) return;

    const messagesQuery = query(
      collection(db, "messages"),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const allMsgs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setMessages(allMsgs);
    });

    return () => unsubscribe();
  }, [userDetails?.id, refreshChat]);

  // FIXED: Real-time messages listener for the left panel - only show users with messages
  useEffect(() => {
    if (!userDetails?.id) return;

    const messagesQuery = query(
      collection(db, "messages"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(messagesQuery,
      (snapshot) => {
        const allMsgs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const partnersMap = {};
        const unread = {};

        allMsgs.forEach((msg) => {
          if (!msg.chatId) return;

          const ids = msg.chatId.split("_");
          if (ids.length !== 2) return;

          // CRITICAL FIX: Check if current user is actually involved in this chat
          if (!ids.includes(userDetails.id)) return;

          const otherUserId = ids.find((id) => id !== userDetails.id);
          if (!otherUserId) return;

          const user = chatUsers.find(u => u.student_id === otherUserId) || {
            student_id: otherUserId,
            name: msg.userId === otherUserId ? msg.userName : msg.toUserName || "User",
            profile_picture: ""
          };

          if (!partnersMap[otherUserId]) {
            partnersMap[otherUserId] = {
              student_id: user.student_id,
              name: user.name,
              profile_picture: user.profile_picture || "",
              lastMessage: msg.text,
              lastMessageTime: msg.createdAt?.toDate()
            };
          }

          if (msg.toUserId === userDetails.id && !msg.read) {
            unread[otherUserId] = (unread[otherUserId] || 0) + 1;
          }
        });

        const partnersFromMessages = Object.values(partnersMap);
        
        // FIXED: Only show users that have actual message history with current user
        setChatPartners(partnersFromMessages);

        setUnreadCounts(unread);
        setLoading(false);
      },
      (error) => {
        console.error("Conversations listener error:", error);
      }
    );

    return () => unsubscribe();
  }, [userDetails?.id, chatUsers]);

  // Filtered messages for the selected chat only
  const filteredMessages = messages.filter(msg => {
    const chatId = [userDetails?.id, selectedChatId].sort().join("_");
    return msg.chatId === chatId;
  });

  useEffect(() => {
    if (!selectedChatId || !userDetails?.id || filteredMessages.length === 0) return;

    const unreadMsgs = filteredMessages.filter(msg => msg.toUserId === userDetails.id && !msg.read);

    if (unreadMsgs.length === 0) return;

    const markAsRead = async () => {
      const updates = [];
      unreadMsgs.forEach((msg) => {
        updates.push(updateDoc(doc(db, "messages", msg.id), { read: true }));
      });

      try {
        await Promise.all(updates);
        setUnreadCounts((prev) => ({
          ...prev,
          [selectedChatId]: 0,
        }));
      } catch (error) {
        console.error("Error marking messages as read:", error);
      }
    };

    markAsRead();
  }, [filteredMessages, selectedChatId, userDetails?.id]);

  // Firebase blocker listener
  useEffect(() => {
    if (!userDetails?.id) return;
    
    const unsubscribe = onSnapshot(
      doc(db, "blocked", userDetails.id), 
      (snap) => {
        if (snap.exists()) {
          setBlockedUsers(snap.data().blockedUsers || []);
        } else {
          setBlockedUsers([]);
        }
      },
      (error) => {
        console.error("Error listening to blocked users:", error);
        setBlockedUsers([]);
      }
    );
    
    return () => unsubscribe();
  }, [userDetails?.id]);

  // Listen to who has blocked YOU
  useEffect(() => {
    if (!userDetails?.id) return;

    const unsubscribe = onSnapshot(
      collection(db, "blocked"),
      (snapshot) => {
        const blockedByList = [];
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.blockedUsers && data.blockedUsers.includes(userDetails.id)) {
            blockedByList.push(doc.id);
          }
        });
        setBlockedByUsers(blockedByList);
      },
      (error) => {
        console.error("Error listening to blocked by users:", error);
        setBlockedByUsers([]);
      }
    );

    return () => unsubscribe();
  }, [userDetails?.id]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [filteredMessages]);

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedChatId || !userDetails?.id || isSending) return;

    setIsSending(true);

    if (blockedUsers.includes(selectedChatId)) {
      alert("You've blocked this user—unblock to send messages.");
      setIsSending(false);
      return;
    }

    if (blockedByUsers.includes(selectedChatId)) {
      alert("This user has blocked you—cannot send messages.");
      setIsSending(false);
      return;
    }

    try {
      const chatId = [userDetails.id, selectedChatId].sort().join('_');
      let toUserName = selectedChat?.name || "User";
      
      if (selectedChat?.isNewUser) {
        const actualUser = chatUsers.find(u => u.student_id === selectedChatId);
        if (actualUser) {
          toUserName = actualUser.name;
        }
      }

      await addDoc(collection(db, "messages"), {
        chatId,
        text: message,
        createdAt: serverTimestamp(),
        userId: userDetails.id,
        userName: userDetails.name,
        userEmail: userDetails.email,
        toUserId: selectedChatId,
        toUserName: toUserName,
        read: false,
      });

      setMessage("");
      setRefreshChat(prev => prev + 1);
    } catch (error) {
      console.error("Failed to send message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleBlockUnblock = async () => {
    if (!selectedChatId || !userDetails?.id || isBlockActionLoading) return;

    setIsBlockActionLoading(true);
    const isCurrentlyBlocked = blockedUsers.includes(selectedChatId);

    try {
      const userBlockDocRef = doc(db, "blocked", userDetails.id);
      const docSnap = await getDoc(userBlockDocRef);
      
      if (!docSnap.exists()) {
        await setDoc(userBlockDocRef, {
          blockedUsers: isCurrentlyBlocked ? [] : [selectedChatId],
        });
      } else {
        await updateDoc(userBlockDocRef, {
          blockedUsers: isCurrentlyBlocked
            ? arrayRemove(selectedChatId)
            : arrayUnion(selectedChatId),
        });
      }
      
    } catch (error) {
      console.error("Error updating block status:", error);
      alert("Failed to update block status. Please try again.");
    } finally {
      setIsBlockActionLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedChatId(user.student_id);
    
    // Clear URL parameters and update URL to reflect current chat
    if (user.student_id !== initialProductChatId) {
      // If selecting a different chat than the initial product chat, clear all params
      navigate('/myaccount/chat', { replace: true });
      // Reset product inquiry state for other chats
      setProductDetails(null);
      setAutoMessageSent(false);
    } else {
      // If going back to the initial product chat, keep minimal URL
      navigate(`/myaccount/chat?user_id=${user.student_id}`, { replace: true });
    }
  };

  const handleBackClick = () => {
    setSelectedChatId(null);
    // Clear URL parameters when going back to chat list
    navigate('/myaccount/chat', { replace: true });
    // Reset auto message state when going back to chat list
    setAutoMessageSent(false);
    setProductDetails(null);
    setInitialProductChatId(null);
  };

  // Function to render message text with clickable links
  const renderMessageWithLinks = (text, isCurrentUser = false) => {
    // URL detection regex
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: isCurrentUser ? '#ffffff' : '#ce55b9',
              textDecoration: 'underline',
              fontWeight: '600',
              cursor: 'pointer',
              backgroundColor: isCurrentUser ? 'rgba(255, 255, 255, 0.1)' : 'rgba(206, 85, 185, 0.1)',
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block',
              margin: '1px',
              transition: 'all 0.2s ease'
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = isCurrentUser ? 'rgba(255, 255, 255, 0.2)' : 'rgba(206, 85, 185, 0.2)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = isCurrentUser ? 'rgba(255, 255, 255, 0.1)' : 'rgba(206, 85, 185, 0.1)';
            }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  if (!userDetails) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress size={48} sx={{ color: '#ce55b9' }} />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 2 }}>
      <div style={{ width: '100%'}}>
      <div className="container">
        <div className="row">
      {/* Left sidebar - Chat list */}
      <Grid item xs={12} lg={4} sx={{
        display: { xs: selectedChatId ? "none" : "block", lg: "block" },
        height: "100%"
      }}>
        <Paper elevation={0} sx={styles.sidebar}>
          {/* Sidebar Header */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#1e293b', 
                fontWeight: 700,
                fontSize: '1.5rem',
                mb: 2
              }}
            >
              Messages
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <TextField
                placeholder="Search conversations..."
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#1e293b',
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#ce55b9',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#ce55b9',
                    }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#64748b',
                    opacity: 1
                  }
                }}
              />
            </Box>
          </Box>

          {/* Chat List */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress sx={{ color: '#ce55b9' }} />
              </Box>
            ) : (
              <Box sx={{ height: '100%', overflowY: 'auto', py: 1 }}>
                {chatPartners.length > 0 ? (
                  chatPartners.map((chatUser, index) => {
                    const unread = unreadCounts[chatUser.student_id] || 0;
                    const isUserBlocked = blockedUsers.includes(chatUser.student_id);
                    const isUserBlockedBy = blockedByUsers.includes(chatUser.student_id);
                    
                    return (
                      <Box
                        key={index}
                        sx={{
                          ...styles.chatListItem,
                          p: 2,
                          mx: 2,
                          my: 1,
                          backgroundColor: selectedChatId === chatUser.student_id 
                            ? '#f1f5f9' 
                            : 'transparent',
                          opacity: isUserBlocked || isUserBlockedBy ? 0.6 : 1,
                          border: selectedChatId === chatUser.student_id 
                            ? '2px solid #ce55b9' 
                            : '2px solid transparent',
                        }}
                        onClick={() => handleUserSelect(chatUser)}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <Badge
                            color="error"
                            badgeContent={unread}
                            invisible={unread === 0}
                            overlap="circular"
                          >
                            <Avatar
                              src={chatUser.profile_picture}
                              sx={{ 
                                width: 52, 
                                height: 52,
                                border: '3px solid',
                                borderColor: selectedChatId === chatUser.student_id 
                                  ? '#ce55b9' 
                                  : '#e2e8f0'
                              }}
                            >
                              {chatUser.name?.charAt(0)}
                            </Avatar>
                          </Badge>
                          
                          <Box flex={1} minWidth={0}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <Typography 
                                fontWeight="600" 
                                noWrap 
                                sx={{ 
                                  fontSize: '1rem',
                                  color: '#1e293b'
                                }}
                              >
                                {chatUser.name}
                              </Typography>
                              {isUserBlocked && (
                                <Chip 
                                  label="Blocked" 
                                  size="small" 
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.7rem',
                                    backgroundColor: alpha('#ff5722', 0.2),
                                    color: '#ff5722',
                                    border: '1px solid #ff5722'
                                  }}
                                />
                              )}
                              {isUserBlockedBy && (
                                <Chip 
                                  label="Blocked you" 
                                  size="small" 
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.7rem',
                                    backgroundColor: alpha('#ff9800', 0.2),
                                    color: '#ff9800',
                                    border: '1px solid #ff9800'
                                  }}
                                />
                              )}
                            </Box>
                            
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography 
                                variant="body2" 
                                noWrap 
                                sx={{ 
                                  fontSize: '0.85rem',
                                  color: '#64748b'
                                }}
                              >
                                {isUserBlocked ? "You blocked this user" : 
                                 isUserBlockedBy ? "This user blocked you" :
                                 (chatUser.lastMessage || "No messages yet")}
                              </Typography>
                              {chatUser.lastMessageTime && !isUserBlocked && !isUserBlockedBy && (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontSize: '0.75rem',
                                    color: '#94a3b8'
                                  }}
                                >
                                  {chatUser.lastMessageTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box 
                    display="flex" 
                    flexDirection="column" 
                    justifyContent="center" 
                    alignItems="center" 
                    height="200px"
                    sx={{ textAlign: 'center', p: 3 }}
                  >
                    <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                      No conversations yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Start a conversation to see your chats here
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>

      {/* Right side - Chat window */}
      <Grid item xs={12} lg={8} sx={{
        display: { xs: selectedChatId ? "block" : "none", lg: "block" },
        height: "100%"
      }}>
        <Paper elevation={0} sx={styles.chatWindow}>
          {selectedChat ? (
            <>
              {/* Enhanced Header */}
              <Box 
                sx={{ 
                  p: 3,
                  background: '#ce55b9',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <IconButton 
                    onClick={handleBackClick}
                    sx={{ 
                      bgcolor: alpha('#ffffff', 0.2),
                      color: 'white',
                      '&:hover': { 
                        bgcolor: alpha('#ffffff', 0.3),
                        transform: 'scale(1.05)'
                      },
                      width: 44,
                      height: 44,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <ArrowBack />
                  </IconButton>
                  
                  <Avatar
                    src={selectedChat.profile_picture}
                    sx={{ 
                      width: 48, 
                      height: 48,
                      border: '3px solid white'
                    }}
                  >
                    {selectedChat.name?.charAt(0)}
                  </Avatar>
                  
                  <Box>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.2rem' }}>
                      {selectedChat.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.8) }}>
                      {isBlocked || isBlockedBy ? 'Chat restricted' : 'Online'}
                    </Typography>
                    {(isBlocked || isBlockedBy) && (
                      <Box display="flex" gap={1} mt={0.5}>
                        {isBlocked && (
                          <Chip 
                            label="Blocked by you" 
                            size="small" 
                            sx={{ 
                              height: 22, 
                              fontSize: '0.7rem',
                              backgroundColor: alpha('#ff5722', 0.2),
                              color: '#ffcdd2'
                            }}
                          />
                        )}
                        {isBlockedBy && (
                          <Chip 
                            label="Blocked you" 
                            size="small" 
                            sx={{ 
                              height: 22, 
                              fontSize: '0.7rem',
                              backgroundColor: alpha('#ff9800', 0.2),
                              color: '#ffe0b2'
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box display="flex" gap={1}>
                  <Tooltip title={isBlocked ? "Unblock this user" : "Block this user"}>
                    <IconButton
                      onClick={handleBlockUnblock}
                      disabled={isBlockActionLoading}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: alpha('#ffffff', 0.2),
                        color: 'white',
                        borderRadius: '12px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha('#ffffff', 0.3),
                          transform: 'scale(1.05)'
                        },
                        '&:disabled': {
                          backgroundColor: alpha('#ffffff', 0.1),
                          color: alpha('#ffffff', 0.5)
                        }
                      }}
                    >
                      {isBlockActionLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : isBlocked ? (
                        <PersonAdd fontSize="small" />
                      ) : (
                        <Block fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  
                  <IconButton
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: alpha('#ffffff', 0.2),
                      color: 'white',
                      borderRadius: '12px',
                      '&:hover': {
                        backgroundColor: alpha('#ffffff', 0.3),
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Messages Area */}
              <Box sx={styles.messageBackground}>
                <Box
                  sx={{ 
                    height: 'calc(100vh - 380px)',
                    overflowY: 'auto',
                    p: 3,
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {filteredMessages.length === 0 ? (
                    <Box 
                      display="flex" 
                      flexDirection="column" 
                      justifyContent="center" 
                      alignItems="center" 
                      height="100%"
                      sx={{ textAlign: 'center' }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: '#ce55b9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3
                        }}
                      >
                        <Send sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography variant="h6" fontWeight="600" color="textSecondary" sx={{ mb: 1 }}>
                        {isBlocked ? "You have blocked this user" : 
                         isBlockedBy ? "This user has blocked you" :
                         "No messages yet"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {!isBlocked && !isBlockedBy && "Start a conversation and say hello!"}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {filteredMessages.map((msg) => (
                        <Box
                          key={msg.id}
                          display="flex"
                          gap={2}
                          mb={2}
                          flexDirection={msg.userId === userDetails.id ? "row-reverse" : "row"}
                          alignItems="flex-end"
                        >
                          <Avatar
                            src={msg.userId === userDetails.id ? null : selectedChat.profile_picture}
                            sx={{ width: 36, height: 36 }}
                          >
                            {msg.userId === userDetails.id ? userDetails.name.charAt(0) : msg.userName?.charAt(0) || "U"}
                          </Avatar>
                          
                          <Box sx={{
                            background: msg.userId === userDetails.id 
                              ? '#ce55b9'
                              : '#ffffff',
                            color: msg.userId === userDetails.id ? 'white' : '#1e293b',
                            padding: '12px 18px',
                            borderRadius: msg.userId === userDetails.id 
                              ? '20px 20px 6px 20px' 
                              : '20px 20px 20px 6px',
                            maxWidth: '75%',
                            wordWrap: 'break-word',
                            boxShadow: msg.userId === userDetails.id 
                              ? '0 4px 20px rgba(206, 85, 185, 0.3)'
                              : '0 4px 20px rgba(0,0,0,0.1)',
                            border: msg.userId === userDetails.id ? 'none' : '1px solid #e2e8f0',
                            position: 'relative'
                          }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontSize: '0.95rem',
                                lineHeight: 1.6,
                                mb: 0.5,
                                whiteSpace: 'pre-line'
                              }}
                            >
                              {renderMessageWithLinks(msg.text, msg.userId === userDetails.id)}
                            </Typography>
                            {msg.createdAt && (
                              <Typography
                                variant="caption"
                                display="block"
                                textAlign={msg.userId === userDetails.id ? "right" : "left"}
                                sx={{
                                  color: msg.userId === userDetails.id 
                                    ? alpha('#ffffff', 0.8)
                                    : alpha('#1e293b', 0.6),
                                  fontSize: '0.75rem',
                                  mt: 0.5
                                }}
                              >
                                {msg.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </Box>
              </Box>

              {/* Enhanced Message Input */}
              <Box
                sx={{
                  p: 3,
                  backgroundColor: '#ffffff',
                  borderTop: '1px solid #e2e8f0'
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    fullWidth
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      isBlocked ? "Unblock to send messages" :
                      isBlockedBy ? "This user has blocked you" :
                      "Type your message..."
                    }
                    disabled={isSending || isBlocked || isBlockedBy}
                    onKeyDown={(e) => e.key === "Enter" && !isSending && !isBlocked && !isBlockedBy && handleSendMessage()}
                    sx={styles.messageInput}
                  />

                  <IconButton
                    onClick={handleSendMessage}
                    disabled={!message.trim() || isSending || isBlocked || isBlockedBy}
                    sx={{
                      background: '#ce55b9',
                      color: 'white',
                      width: 48,
                      height: 48,
                      '&:hover': {
                        background: '#b84ca3',
                        transform: 'scale(1.05)'
                      },
                      '&:disabled': {
                        backgroundColor: '#e2e8f0',
                        color: '#94a3b8'
                      },
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 20px rgba(206, 85, 185, 0.3)'
                    }}
                  >
                    {isSending ? <CircularProgress size={24} color="inherit" /> : <Send />}
                  </IconButton>
                </Box>
              </Box>
            </>
          ) : (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="100%"
              sx={styles.messageBackground}
            >
              <Box textAlign="center" sx={{ zIndex: 1 }}>
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: '#ce55b9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    mx: 'auto'
                  }}
                >
                  <Send sx={{ fontSize: 60, color: 'white' }} />
                </Box>
                <Typography variant="h5" fontWeight="600" color="textSecondary" sx={{ mb: 2 }}>
                  {loading ? 'Loading conversations...' : 'Select a chat to start messaging'}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Choose a conversation from the sidebar to begin chatting
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>
      </div>
      </div>
      </div>
    </Grid>
  );
};

export default Chat;